const noop = () => {}; // no-op
const trueCond = () => true;
const randInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

/**
 * @class SequenceManager
 * @description
 * Manages an array of steps to be resolved sequencially
 */
export class SequenceManager {
  /**
   * @param {number} [delay=1] - The delay between steps in milliseconds
   * @param {Object=} [options] - Extra options
   * @param {number=} [options.rand=0] - A random offset + or - the delay
   * @param {Function=} [options.onStepComplete] - A random offset + or - the delay
   */
  constructor(delay = 1, options = {}) {
    this.delay = delay;
    this.steps = [];
    this.rand = options.rand || 0;
    this.onStepComplete = options.onStepComplete || noop;
  }

  /**
   * Add a reference redux status to the array of steps
   * @param {string=} [label=null] - The step label
   * @param {Function=} condition - A function that returns a boolean representing the condition
   *                                for completing this step
   * @param {Function=} onComlete - Callback for step completion
   * @returns {SequenceManager} this - A reference to the called class for chaining
   */
  addStep(label = null, condition = trueCond, onComplete = null) {
    let prevStep = this.steps[this.steps.length - 1];
    let step = new Step(
      label,
      prevStep,
      condition,
      { delay: this.delay, rand: this.rand },
      onComplete || this.onStepComplete
    );
    this.steps.push(step);
    return this;
  }

  /**
   * The same as calling addStep multiple times
   * @param {Object[]} steps - The same as a step
   * @param {String=} [steps[].label=null] - The step label
   * @param {Function=} steps[].refStatus - The reference redux status
   */
  addSteps(steps) {
    for (let step of steps) {
      this.addStep(step.label, step.condition);
    }
    return this.steps;
  }

  /**
   * Determine if the last step has been completed (dependent on all other steps)
   * returns false if there are no steps
   * @returns {Boolean}
   */
  allComplete() {
    if (this.steps.length === 0) {
      return false;
    }
    // Check starting from the first step
    // if the first isn't complete, the last isn't
    // if the last step isn't complete it needs to check all other steps until one is complete
    return this.steps.every(step => step.isComplete());
  }

  /**
   * Stop processing steps
   */
  halt() {
    for (let step of this.steps) {
      step.halt();
    }
  }

  /**
   * Reset the steps
   */
  reset() {
    for (let step of this.steps) {
      step.reset();
    }
  }
}

/**
 * @class Step
 * @description
 * A step to be executed sequentially
 */
class Step {
  /**
   * @param {String} label - The step label
   * @param {Step|null} prevStep - The previous step
   * @param {Object} refStatus - a reference redux Status
   * @param {Object} [options] - Include options like delay,
   *                           typically set by the SequenceManager class.
   * @param {Number} [options.delay] - The delay in ms
   * @param {Number} [options.rand] - A random delay to be added to delay
   */
  constructor(label, prevStep, condition = trueCond, options = {}, onStepComplete = noop) {
    this.label = label;
    this.prevStep = prevStep;
    this.condition = condition;
    this.delay = options.delay || 1;
    this.rand = options.rand || 0;
    this.onStepComplete = onStepComplete;

    this.delayTimeout = null;
    this.delayComplete = false;
    this.conditionSatisfied = false;
  }

  /**
   * Call the user's callback, unless it has been called
   */
  callOnStepComplete() {
    if (!this.onStepCompleteCalled) {
      this.onStepCompleteCalled = true;
      this.onStepComplete(this);
    }
  }

  /**
   * Check for completion of the last step
   * @returns {Boolean}
   */
  isPreviousComplete() {
    if (!this.prevStep) {
      return true;
    }
    return this.prevStep.isComplete();
  }

  /**
   * Returns a memoized evaluation of the consumer condition function
   * @returns {Boolean}
   */
  isConditionSatisfied() {
    if (this.conditionSatisfied) {
      return true;
    }
    this.conditionSatisfied = this.condition(this);
    return this.conditionSatisfied;
  }

  /**
   * Check if the delay is complete based on the previous step, or
   * start a new delay timeout if it hasn't been started
   * @returns {Boolean} delayComplete
   */
  isDelayComplete() {
    if (!this.delayComplete && !this.didStartDelay) {
      this.didStartDelay = true;
      let delay = this.delay + randInt(-this.rand, this.rand);
      this.delayTimeout = setTimeout(() => {
        this.delayComplete = true;
        this.isComplete(); // calls onStepComplete if conditionSatisfied
      }, delay);
    }
    return this.delayComplete;
  }

  /**
   * If the previous step was complete, this starts the delay,
   * if the delay is complete returns true
   * @returns Boolean
   */
  isComplete() {
    if (this.isPreviousComplete() && this.isDelayComplete() && this.isConditionSatisfied()) {
      this.callOnStepComplete(this);
      return true;
    }
    return false;
  }

  /**
   * Stop this step's timer
   */
  halt() {
    if (this.delayTimeout) {
      clearTimeout(this.delayTimeout);
      this.delayTimeout = null;
    }
  }

  /**
   * Resets the step
   */
  reset() {
    this.halt();
    this.conditionSatisfied = false;
    this.didStartDelay = false;
    this.delayComplete = false;
    this.onStepCompleteCalled = false;
  }
}
